import { useState, useMemo } from "react";
import {
  LayoutDashboard, Users, Users2, LogOut, Eye, EyeOff, Loader2,
  ChevronRight, Phone, Mail, Building2, Tag, Clock,
  BarChart3, AlertCircle, Calendar, MapPin, Video, User, RefreshCw, Save, X,
  FolderOpen, FileText, ExternalLink, CheckCircle, XCircle, ChevronDown, Repeat2, Download,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import LeadDetailDrawer from "@/components/LeadDetailDrawer";
import WeeklyCalendarView, { type CalendarEvent } from "@/components/WeeklyCalendarView";
import { resolveS360Name, stripRepCode } from "@/lib/s360Name";
import { parseVerticalsClient } from "@/lib/teamVerticals";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import AgreementCompanyQueue from "@/components/AgreementCompanyQueue";

// ─── Types ────────────────────────────────────────────────────────────────────
interface TeamMemberSession {
  id: number;
  name: string;
  email: string;
  assignedVertical: string;
  // Parsed/validated list from the server (teamMembers.login), present on any
  // session created after the multi-vertical fix. Older sessions cached in
  // sessionStorage from before this shipped won't have it - see
  // parseVerticalsClient below for the fallback.
  assignedVerticals?: string[];
  ghlUserId: string | null;
  role: "team_member" | "product_manager";
}

// ─── Stage helpers ────────────────────────────────────────────────────────────
const STAGE_COLORS: Record<string, string> = {
  // Generic / Fundable360
  lead_captured: "#6B7280",
  audit_submitted: "#3B82F6",
  follow_up_active: "#F59E0B",
  payment_received: "#8B5CF6",
  onboarding_submitted: "#06B6D4",
  active_client: "#10B981",
  lost_cold: "#EF4444",
  // Scale360
  new_lead: "#6B7280",
  initial_outreach: "#3B82F6",
  appointment_booked: "#F59E0B",
  appointment_completed: "#8B5CF6",
  qualified: "#06B6D4",
  enrolled: "#84ab4a",
  onboarding: "#10B981",
  funded: "#059669",
  graduated: "#0D9488",
  inactive: "#9CA3AF",
  // Legacy360 / Media360 / Share360
  contacted: "#3B82F6",
  proposal_sent: "#8B5CF6",
  in_review: "#F59E0B",
  approved: "#10B981",
  declined: "#EF4444",
  cancelled: "#EF4444",
  // GHL stage name fallback already handled by ghlStageName display
};
function stageColor(s: string | null | undefined) { if (!s) return "#84ab4a"; return STAGE_COLORS[s.toLowerCase().replace(/[\s-]/g, "_")] ?? "#84ab4a"; }
function stageLabel(s: string | null | undefined) { if (!s) return "Unknown"; return s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()); }

// ─── Client-side recurring event expansion for calendar (120-day window) ──────
function expandEventsForCalendar(events: any[]): any[] {
  const windowStart = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const windowEnd   = Date.now() + 90 * 24 * 60 * 60 * 1000;
  const result: any[] = [];
  for (const ev of events) {
    if (!ev.recurrenceRule || ev.recurrenceRule === "none") { result.push(ev); continue; }
    const duration = ev.endTimeUtc - ev.startTimeUtc;
    const seriesEnd = ev.recurrenceEndDate ? Math.min(ev.recurrenceEndDate, windowEnd) : windowEnd;
    let cur = ev.startTimeUtc; let idx = 0;
    while (cur <= seriesEnd) {
      if (cur + duration >= windowStart) {
        result.push({ ...ev, id: idx === 0 ? ev.id : `${ev.id}-${cur}`, startTimeUtc: cur, endTimeUtc: cur + duration });
      }
      const d = new Date(cur);
      if (ev.recurrenceRule === "daily") d.setDate(d.getDate() + 1);
      else if (ev.recurrenceRule === "weekly") d.setDate(d.getDate() + 7);
      else if (ev.recurrenceRule === "monthly") d.setMonth(d.getMonth() + 1);
      else break;
      cur = d.getTime(); idx++;
      if (idx > 730) break;
    }
  }
  return result;
}
function timeAgo(date: Date | string) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function TeamMemberLogin({ onLogin }: { onLogin: (session: TeamMemberSession) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");

  const loginMutation = trpc.teamMembers.login.useMutation({
    onSuccess: (data) => {
      onLogin({ ...data.member, ghlUserId: (data.member as any).ghlUserId ?? null, role: (data.member as any).role ?? "team_member" });
      toast.success(`Welcome back, ${data.member.name}!`);
    },
    onError: (e) => setError(e.message),
  });

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setError("");
    loginMutation.mutate({ email, password });
  };

  return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#84ab4a]/15 border border-[#84ab4a]/30 flex items-center justify-center mx-auto mb-4">
            <LayoutDashboard className="w-7 h-7 text-[#84ab4a]" />
          </div>
          <div className="text-2xl font-bold text-white">Team Portal</div>
          <div className="text-sm text-white/40 mt-1">BizConnect360 Staff Access</div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-white/60 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="you@bizconnect360.com"
              className="w-full bg-white/5 border border-white/15 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#84ab4a]/50 focus:ring-1 focus:ring-[#84ab4a]/30 transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-white/60 mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full bg-white/5 border border-white/15 rounded-xl px-3.5 py-2.5 pr-10 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#84ab4a]/50 focus:ring-1 focus:ring-[#84ab4a]/30 transition-all"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {error === "Invalid credentials or account inactive"
                  ? "Email or password is incorrect. If you don't have an account yet, ask your admin to create one in the Team Members tab."
                  : error}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full bg-[#84ab4a] hover:bg-[#6e9038] disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {loginMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sign In
          </button>
        </form>

        <p className="text-center text-xs text-white/25 mt-6">
          Accounts are created by admin in the Admin Dashboard → Team Members tab.
        </p>
      </div>
    </div>
  );
}

// ─── Agents Tab ──────────────────────────────────────────────────────────────
function AgentsTab({ vertical, onAgentClick }: { vertical: string; onAgentClick?: (agent: any) => void }) {
  const { data: agents = [], isLoading } = trpc.rep.listByVertical.useQuery(
    { vertical },
    { refetchInterval: 120_000 }
  );
  const [agentSearch, setAgentSearch] = useState("");

  const LEVEL_COLORS: Record<string, string> = {
    "Agency": "#C8920A",
    "Senior Associate": "#1A6FD4",
    "Associate": "#84ab4a",
  };

  const displayedAgents = agentSearch.trim()
    ? (agents as any[]).filter((a: any) => {
        const q = agentSearch.toLowerCase();
        return (
          `${a.firstName ?? ""} ${a.lastName ?? ""}`.toLowerCase().includes(q) ||
          a.repCode?.toLowerCase().includes(q) ||
          a.email?.toLowerCase().includes(q)
        );
      })
    : agents;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{vertical} Agents</h2>
          <p className="text-sm text-gray-500 mt-0.5">Active sales agents assigned to this vertical.</p>
        </div>
        <input
          type="text"
          placeholder="Search by name, rep code, or email…"
          value={agentSearch}
          onChange={e => setAgentSearch(e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 w-56 focus:outline-none focus:border-[#84ab4a]/50"
        />
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading agents…
        </div>
      ) : displayedAgents.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <div className="text-sm font-medium">{agentSearch ? "No agents match your search" : `No active agents for ${vertical}`}</div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Name</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Rep Code</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Level</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Contact</th>
                <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(displayedAgents as any[]).map((agent: any) => (
                <tr key={agent.id} onClick={() => onAgentClick?.(agent)} className={`hover:bg-gray-50 transition-colors ${onAgentClick ? "cursor-pointer" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">{agent.firstName} {agent.lastName}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-lg">{agent.repCode}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: `${LEVEL_COLORS[agent.agentLevel] ?? "#84ab4a"}15`, color: LEVEL_COLORS[agent.agentLevel] ?? "#84ab4a" }}
                    >
                      {agent.agentLevel ?? "Associate"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-500 space-y-0.5">
                      {agent.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{agent.email}</div>}
                      {agent.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{agent.phone}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {agent.createdAt ? new Date(agent.createdAt).toLocaleDateString() : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Staff ID Setup Panel (shown when ghlUserId not yet linked) ───────────────
function StaffIdSetupPanel({ memberId, onSaved }: { memberId: number; onSaved: (id: string) => void }) {
  const [staffInput, setStaffInput] = useState("");
  const setGhlId = trpc.teamMembers.setGhlUserId.useMutation({
    onSuccess: () => {
      const id = staffInput.trim();
      console.log(`[Appointments] Staff ID linked staffId=${id} memberId=${memberId} status=success`);
      toast.success("Staff ID linked - loading your appointments…");
      onSaved(id);
    },
    onError: () => toast.error("Failed to save Staff ID - please try again"),
  });

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-2 text-amber-800 font-bold text-sm">
        <AlertCircle className="w-4 h-4" /> Appointments Not Linked
      </div>
      <p className="text-xs text-amber-700 mb-4">
        Enter your Staff User ID from Wibiz (Settings → My Staff → your profile URL) to load your appointments.
        You can also ask your admin to link it from the Team Members tab.
      </p>
      <div className="flex items-center gap-2">
        <input
          className="flex-1 text-sm font-mono border border-amber-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-amber-400"
          placeholder="Paste Wibiz Staff User ID"
          value={staffInput}
          onChange={e => setStaffInput(e.target.value)}
        />
        <button
          onClick={() => {
            const id = staffInput.trim();
            if (!id) return;
            console.log(`[Appointments] Linking Staff ID staffId=${id} memberId=${memberId}`);
            setGhlId.mutate({ id: memberId, ghlUserId: id });
          }}
          disabled={!staffInput.trim() || setGhlId.isPending}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-40 transition-colors whitespace-nowrap"
        >
          {setGhlId.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Link & Sync
        </button>
      </div>
    </div>
  );
}

// ─── Appointments Tab ─────────────────────────────────────────────────────────
function AppointmentsTab({ userId, vertical, memberId, onUserIdSaved }: {
  userId: string;
  vertical: string;
  memberId: number;
  onUserIdSaved: (newId: string) => void;
}) {
  const [showResync, setShowResync] = useState(false);
  const [staffInput, setStaffInput] = useState("");
  const [selectedAppt, setSelectedAppt] = useState<any | null>(null);

  // Show ALL appointments for leads in this vertical (across all staff in the vertical).
  // Each appointment includes assignedStaffName so the team member can see who owns it.
  // This is more reliable than filtering by ghlUserId (which depends on Staff ID being set correctly).
  const { data: appointments = [], isLoading, refetch } = trpc.ghlAppointments.getForVertical.useQuery(
    { vertical },
    { refetchInterval: 120_000 }
  );

  const setGhlId = trpc.teamMembers.setGhlUserId.useMutation({
    onSuccess: () => {
      const id = staffInput.trim();
      console.log(`[Appointments] Re-sync: Staff ID updated staffId=${id} memberId=${memberId} status=success`);
      toast.success("Staff ID updated - reloading appointments…");
      onUserIdSaved(id);
      setShowResync(false);
      setStaffInput("");
    },
    onError: () => toast.error("Failed to update Staff ID - please try again"),
  });

  function apptStatusColor(status: string) {
    if (status === "confirmed") return "#10B981";
    if (status === "cancelled") return "#EF4444";
    if (status === "showed") return "#3B82F6";
    if (status === "noshow") return "#F59E0B";
    return "#6B7280";
  }
  function apptStatusLabel(status: string) {
    if (status === "confirmed") return "Confirmed";
    if (status === "cancelled") return "Cancelled";
    if (status === "showed") return "Showed";
    if (status === "noshow") return "No Show";
    return status;
  }
  // GHL returns startTime as ISO 8601 string - normalise to ms for comparisons
  function toMs(val: string | number | null | undefined): number {
    if (!val) return 0;
    if (typeof val === "number") return val < 2_000_000_000 ? val * 1000 : val;
    return new Date(val).getTime();
  }

  function fmtDateTime(val: string | number) {
    return new Date(toMs(val)).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  }

  const active = (appointments as any[]).filter(a => a.status !== "cancelled");
  const now = Date.now();
  const upcoming = active.filter(a => toMs(a.startTime) > now).sort((a, b) => toMs(a.startTime) - toMs(b.startTime));
  const past = active.filter(a => toMs(a.startTime) <= now).sort((a, b) => toMs(b.startTime) - toMs(a.startTime));

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Appointments</h2>
          <p className="text-sm text-gray-500 mt-0.5">All {vertical} appointments - shows owner for each booking. Refreshes every 2 min.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { refetch(); }}
            className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 border border-gray-200 rounded-lg px-3 py-1.5 transition-colors"
          >
            <Calendar className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading appointments…
        </div>
      ) : (appointments as any[]).length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <div className="text-sm font-medium">No appointments found for {vertical}</div>
          <div className="text-xs mt-1">Client appointments booked through {vertical} will appear here.</div>
        </div>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                Upcoming ({upcoming.length})
              </div>
              <div className="space-y-3">
                {upcoming.map((appt: any) => (
                  <AppointmentCard key={appt.id} appt={appt} statusColor={apptStatusColor} statusLabel={apptStatusLabel} fmtDateTime={fmtDateTime} onClick={() => setSelectedAppt(appt)} />
                ))}
              </div>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                Past ({past.length})
              </div>
              <div className="space-y-3">
                {past.slice(0, 20).map((appt: any) => (
                  <AppointmentCard key={appt.id} appt={appt} statusColor={apptStatusColor} statusLabel={apptStatusLabel} fmtDateTime={fmtDateTime} onClick={() => setSelectedAppt(appt)} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {selectedAppt && (
        <AppointmentDetailModal
          appt={selectedAppt}
          onClose={() => setSelectedAppt(null)}
          fmtDateTime={fmtDateTime}
          statusColor={apptStatusColor}
          statusLabel={apptStatusLabel}
        />
      )}
    </div>
  );
}

function AppointmentCard({ appt, statusColor, statusLabel, fmtDateTime, onClick }: {
  appt: any;
  statusColor: (s: string) => string;
  statusLabel: (s: string) => string;
  fmtDateTime: (val: string | number) => string;
  onClick?: () => void;
}) {
  const color = statusColor(appt.status ?? "confirmed");
  return (
    <div onClick={onClick} className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex gap-4 ${onClick ? "cursor-pointer hover:shadow-md hover:border-gray-200 transition-all" : ""}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
        <Calendar className="w-5 h-5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="font-semibold text-sm text-gray-800 truncate">{appt.title ?? "Appointment"}</div>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
            style={{ background: `${color}15`, color }}
          >
            {statusLabel(appt.status ?? "confirmed")}
          </span>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {appt.startTime ? fmtDateTime(appt.startTime) : "-"}
          {appt.endTime && appt.startTime ? ` – ${new Date(appt.endTime).toLocaleTimeString("en-US", { timeStyle: "short" })}` : ""}
          </div>
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
          {(appt.clientName || appt.contact?.name) && (
            <span className="flex items-center gap-1"><User className="w-3 h-3" />{appt.clientName ?? appt.contact?.name}</span>
          )}
          {appt.contact?.phone && (
            <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{appt.contact.phone}</span>
          )}
          {appt.contact?.email && (
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{appt.contact.email}</span>
          )}
          {appt.location && (
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{appt.location}</span>
          )}
          {appt.meetingLink && (
            <a href={appt.meetingLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-500 hover:underline">
              <Video className="w-3 h-3" />Join
            </a>
          )}
          {appt.assignedStaffName && (
            <span className="flex items-center gap-1 font-medium text-gray-700 bg-gray-100 rounded px-1.5 py-0.5">
              <User className="w-3 h-3" /> Owner: {appt.assignedStaffName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Agent Detail Modal ───────────────────────────────────────────────────────
function AgentDetailModal({ agent, onClose }: { agent: any; onClose: () => void }) {
  const LEVEL_COLORS: Record<string, string> = { "Agency": "#C8920A", "Senior Associate": "#1A6FD4", "Associate": "#84ab4a" };
  const lc = LEVEL_COLORS[agent.agentLevel] ?? "#84ab4a";
  const agentEmail = agent.email ?? "";
  const submissionsQuery = trpc.share360.getLeadSubmissions.useQuery(
    { email: agentEmail },
    { enabled: !!agentEmail, staleTime: 60_000 }
  );
  const completedSubs = (submissionsQuery.data ?? []).filter((s: any) => s.status === "completed");

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold" style={{ background: `${lc}15`, color: lc }}>
              {(agent.firstName?.[0] ?? "")}{(agent.lastName?.[0] ?? "")}
            </div>
            <div>
              <div className="font-bold text-gray-900">{agent.firstName} {agent.lastName}</div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${lc}15`, color: lc }}>{agent.agentLevel ?? "Associate"}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-gray-600"><Tag className="w-3.5 h-3.5 text-gray-400" /> <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{agent.repCode}</span></div>
          {agent.email && <div className="flex items-center gap-2 text-gray-600"><Mail className="w-3.5 h-3.5 text-gray-400" />{agent.email}</div>}
          {agent.phone && <div className="flex items-center gap-2 text-gray-600"><Phone className="w-3.5 h-3.5 text-gray-400" />{agent.phone}</div>}
          {agent.vertical && <div className="flex items-center gap-2 text-gray-600"><BarChart3 className="w-3.5 h-3.5 text-gray-400" />Primary: {agent.vertical}</div>}
          {agent.createdAt && <div className="flex items-center gap-2 text-gray-500"><Clock className="w-3.5 h-3.5 text-gray-400" />Joined {new Date(agent.createdAt).toLocaleDateString()}</div>}
        </div>

        {/* DocuSeal submissions signed by this agent */}
        {agentEmail && (
          <div className="mt-5 border-t border-gray-100 pt-4">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-[#1A6FD4]" />
              <span className="text-[#1A6FD4]">Signed Documents</span>
              {!submissionsQuery.isLoading && (
                <span className="ml-1 text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                  {completedSubs.length}
                </span>
              )}
            </div>
            {submissionsQuery.isLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </div>
            ) : completedSubs.length === 0 ? (
              <p className="text-xs text-gray-400">No completed submissions found.</p>
            ) : (
              <div className="space-y-2">
                {completedSubs.map((s: any) => (
                  <div key={s.id} className="flex items-start gap-2 p-2 rounded-lg bg-blue-50/40 border border-blue-100">
                    <FileText className="w-3.5 h-3.5 text-[#1A6FD4] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-gray-800 truncate">
                        {s.templateName ?? `Submission #${s.id}`}
                      </div>
                      {s.completedAt && (
                        <div className="text-[10px] text-gray-400">
                          Completed {new Date(s.completedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </div>
                      )}
                      <div className="mt-1">
                        <TeamSubmissionDocLink submissionId={s.id} status={s.status} />
                      </div>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex-shrink-0">Completed</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Appointment Detail Modal ─────────────────────────────────────────────────
function AppointmentDetailModal({ appt, onClose, fmtDateTime, statusColor, statusLabel }: {
  appt: any; onClose: () => void;
  fmtDateTime: (v: string | number) => string;
  statusColor: (s: string) => string;
  statusLabel: (s: string) => string;
}) {
  const color = statusColor(appt.status ?? "confirmed");
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
              <Calendar className="w-4 h-4" style={{ color }} />
            </div>
            <div>
              <div className="font-bold text-gray-900 text-sm">{appt.title ?? "Appointment"}</div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${color}15`, color }}>{statusLabel(appt.status ?? "confirmed")}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-gray-400" />{appt.startTime ? fmtDateTime(appt.startTime) : "-"}{appt.endTime ? ` – ${new Date(appt.endTime).toLocaleTimeString("en-US", { timeStyle: "short" })}` : ""}</div>
          {(appt.clientName || appt.contact?.name) && <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-gray-400" />{appt.clientName ?? appt.contact?.name}</div>}
          {appt.contact?.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-gray-400" />{appt.contact.email}</div>}
          {appt.contact?.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-gray-400" />{appt.contact.phone}</div>}
          {appt.location && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gray-400" />{appt.location}</div>}
          {appt.assignedStaffName && <div className="flex items-center gap-2"><Users2 className="w-3.5 h-3.5 text-gray-400" />Owner: {appt.assignedStaffName}</div>}
          {appt.notes && <div className="mt-3 text-xs text-gray-500 bg-gray-50 rounded-xl p-3">{appt.notes}</div>}
          {appt.meetingLink && (
            <a href={appt.meetingLink} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors">
              <Video className="w-4 h-4" /> Join Meeting
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Calendar Tab ─────────────────────────────────────────────────────────────
const RECURRENCE_LABELS: Record<string, string> = {
  daily: "Repeats daily",
  weekly: "Repeats weekly",
  monthly: "Repeats monthly",
};

function CalendarTab() {
  // bc360Calendar.list already expands recurring events server-side - do NOT re-expand client-side
  const { data: events = [], isLoading } = trpc.bc360Calendar.list.useQuery(undefined, { staleTime: 60_000 });
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  return (
    <div>
      {/* Event detail modal - same fixed-overlay pattern as AppointmentDetailModal */}
      {selectedEvent && (() => {
        const ev = selectedEvent;
        const color =
          ev.eventType === "Training" ? "#84ab4a"
          : ev.eventType === "Webinar" ? "#1A6FD4"
          : ev.eventType === "Workshop" ? "#E8620A"
          : ev.eventType === "Meeting" ? "#C8920A"
          : "#7B4FD4";
        const isRecurring = ev.recurrenceRule && ev.recurrenceRule !== "none";
        return (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={() => setSelectedEvent(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}18`, border: `1.5px solid ${color}40` }}>
                    <Calendar className="w-5 h-5" style={{ color }} />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{ev.title}</div>
                    {ev.eventType && (
                      <span className="inline-block text-[11px] font-bold px-2 py-0.5 rounded-full mt-0.5" style={{ background: `${color}15`, color }}>
                        {ev.eventType}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => setSelectedEvent(null)} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
              </div>
              <div className="space-y-3 text-sm">
                <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-1">
                  <div className="text-xs text-gray-500 font-medium">
                    {new Date(ev.startTimeUtc).toLocaleDateString("en-US", {
                      weekday: "long", month: "long", day: "numeric", year: "numeric",
                    })}
                  </div>
                  <div className="text-sm font-semibold text-gray-800">
                    {new Date(ev.startTimeUtc).toLocaleTimeString("en-US", {
                      hour: "numeric", minute: "2-digit", timeZoneName: "short",
                    })}
                    {" – "}
                    {new Date(ev.endTimeUtc).toLocaleTimeString("en-US", {
                      hour: "numeric", minute: "2-digit",
                    })}
                  </div>
                  {isRecurring && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 pt-0.5">
                      <Repeat2 className="w-3 h-3 flex-shrink-0" />
                      {RECURRENCE_LABELS[ev.recurrenceRule!] ?? "Recurring event"}
                    </div>
                  )}
                </div>
                {ev.description && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Details</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{ev.description}</p>
                  </div>
                )}
                {ev.joinLink && (
                  <a href={ev.joinLink} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-[#84ab4a] hover:bg-[#6d9138] text-white text-sm font-semibold transition-colors">
                    <ExternalLink className="w-4 h-4" /> Join Event
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      <h2 className="text-xl font-bold text-gray-900 mb-1">BizConnect360 Calendar</h2>
      <p className="text-sm text-gray-500 mb-5">Upcoming company-wide training sessions and events. Click any event for details.</p>
      <div style={{ height: 600 }}>
        <WeeklyCalendarView events={events as CalendarEvent[]} isLoading={isLoading} onEventClick={setSelectedEvent} />
      </div>
    </div>
  );
}

// ─── DocuSeal PDF download link (lazy-loads per submission) ──────────────────
function TeamSubmissionDocLink({ submissionId, status }: { submissionId: number; status: string }) {
  const [fetch, setFetch] = useState(false);
  const query = trpc.share360.getSubmissionAllR2Docs.useQuery(
    { submissionId },
    { enabled: fetch, staleTime: 300_000 }
  );
  if (status !== "completed") return <span className="text-gray-300 text-xs">-</span>;
  if (!fetch) {
    return (
      <button onClick={() => setFetch(true)}
        className="text-[#1A6FD4] text-xs underline flex items-center gap-0.5 hover:text-blue-800 whitespace-nowrap">
        <Download className="w-3 h-3" /> Load PDF
      </button>
    );
  }
  if (query.isLoading) return <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />;
  const docs = query.data?.docs ?? [];
  if (docs.length) {
    return (
      <div className="flex flex-col gap-0.5">
        {docs.map((doc, i) => (
          <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
            className="text-[#84ab4a] flex items-center gap-0.5 hover:underline whitespace-nowrap font-semibold text-xs">
            <Download className="w-3 h-3" /> {doc.name}
          </a>
        ))}
      </div>
    );
  }
  return <button onClick={() => query.refetch()} className="text-red-400 text-xs underline whitespace-nowrap">Retry</button>;
}

// ─── Documents Tab ────────────────────────────────────────────────────────────
function DocumentsTab({ vertical, repNameMap, reviewedBy }: { vertical: string; repNameMap: Record<string, string>; reviewedBy: string }) {
  const { data: allDocs = [], isLoading, refetch } = trpc.clientPortal.adminListDocuments.useQuery(
    { status: "all", vertical },
    { staleTime: 30_000 }
  );
  const [docSearch, setDocSearch] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState<"all" | "business" | "nonprofit">("all");
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set());
  const [docusealOpen, setDocusealOpen] = useState(false);
  const [docusealSearch, setDocusealSearch] = useState("");
  const [reviewingDoc, setReviewingDoc] = useState<any | null>(null);
  const [decision, setDecision] = useState<"approved" | "rejected" | "">("");
  const [note, setNote] = useState("");
  const utils = trpc.useUtils();
  const reviewMutation = trpc.clientPortal.reviewDocument.useMutation({
    onSuccess: () => {
      toast.success(decision === "approved" ? "Document approved" : "Document rejected");
      setReviewingDoc(null);
      setDecision("");
      setNote("");
      refetch();
      utils.clientPortal.adminListDocuments.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const docusealQuery = trpc.share360.adminListSubmissions.useQuery({ limit: 200 }, {
    // Server now rejects team-member callers with no Share360 access
    // (FORBIDDEN) - only fetch when this tab is actually scoped to Share360,
    // otherwise every non-Share360 team member would hit an error on load.
    enabled: vertical.startsWith("Share360"),
    staleTime: 60_000,
  });
  const docusealSubmissionRows = docusealQuery.data?.submissions ?? [];
  // Filter DocuSeal submissions by this team's vertical (by template name prefix)
  const verticalRoot = vertical.split(" ")[0]; // "Share360 Business" → "Share360"

  // Search runs SERVER-side across DocuSeal history. This tab has no "Load
  // More" at all, so it previously could only ever search the most recent 200
  // submissions - anything older was simply unreachable from here.
  const debouncedDocusealSearch = useDebouncedValue(docusealSearch, 400);
  const docusealSearchTerm = debouncedDocusealSearch.trim();
  const isDocusealSearching = docusealSearchTerm.length >= 2;
  const docusealSearchQuery = trpc.share360.adminSearchSubmissions.useQuery(
    { query: docusealSearchTerm, limit: 100 },
    {
      enabled: vertical.startsWith("Share360") && docusealOpen && isDocusealSearching,
      staleTime: 60_000,
      placeholderData: prev => prev,
    },
  );

  // The vertical template-name scope applies to search results exactly as it
  // does to the browse list - searching must not widen what this team can see.
  const matchesVertical = (s: any) =>
    (s.templateName ?? "").toLowerCase().includes(verticalRoot.toLowerCase());
  const docusealSubmissions = isDocusealSearching
    ? (docusealSearchQuery.data?.submissions ?? []).filter(matchesVertical)
    : docusealSubmissionRows.filter(matchesVertical);

  // Map submitter email → completed submissions (for inline display per lead accordion)
  const submissionsByEmail = useMemo(() => {
    const map = new Map<string, any[]>();
    docusealSubmissionRows.filter((s: any) => s.status === "completed").forEach((s: any) => {
      const email = (s.submitterEmail ?? "").toLowerCase().trim();
      if (!email) return;
      if (!map.has(email)) map.set(email, []);
      map.get(email)!.push(s);
    });
    return map;
  }, [docusealSubmissionRows]);

  const isShare360Vertical = vertical.startsWith("Share360");

  // Filter to this vertical, then apply type + search filters
  const docs = (allDocs as any[]).filter(d => {
    const v = d.leadVerticalName ?? "";
    if (isShare360Vertical ? !v.startsWith("Share360") : v !== vertical) return false;
    if (isShare360Vertical && docTypeFilter !== "all") {
      if (docTypeFilter === "business" && v !== "Share360 Business") return false;
      if (docTypeFilter === "nonprofit" && v !== "Share360 Nonprofit") return false;
    }
    if (docSearch.trim()) {
      const q = docSearch.toLowerCase();
      const repName = repNameMap[d.leadRepCode ?? ""] ?? "";
      const clientName = `${d.leadFirstName ?? ""} ${d.leadLastName ?? ""}`.trim();
      if (
        !clientName.toLowerCase().includes(q) &&
        !repName.toLowerCase().includes(q) &&
        !(d.leadRepCode ?? "").toLowerCase().includes(q) &&
        !(d.leadBusinessName ?? "").toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  // Group by leadId
  const clientGroups = useMemo(() => {
    const map = new Map<number, any[]>();
    docs.forEach((doc: any) => {
      const id = doc.leadId ?? 0;
      if (!map.has(id)) map.set(id, []);
      map.get(id)!.push(doc);
    });
    return Array.from(map.entries()).map(([leadId, clientDocs]) => ({ leadId, docs: clientDocs }));
  }, [docs]);

  const toggleClient = (leadId: number) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };

  const DOC_STATUS_LABEL: Record<string, string> = { submitted: "Submitted", pending: "Submitted", approved: "Approved", rejected: "Rejected" };
  const DOC_STATUS_STYLE: Record<string, string> = {
    submitted: "bg-blue-50 text-blue-700 border border-blue-200",
    pending:   "bg-blue-50 text-blue-700 border border-blue-200",
    approved:  "bg-emerald-50 text-emerald-700 border border-emerald-200",
    rejected:  "bg-red-50 text-red-700 border border-red-200",
  };

  function clientTypeBadge(vName: string) {
    if (vName === "Share360 Business") return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Business</span>;
    if (vName === "Share360 Nonprofit") return <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">Nonprofit</span>;
    return null;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Client Documents</h2>
          <p className="text-sm text-gray-500 mt-0.5">Documents uploaded by clients through the {vertical} portal.</p>
        </div>
        <button onClick={() => refetch()} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 border border-gray-200 rounded-lg px-3 py-1.5 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Search + type filter */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          type="text"
          placeholder="Search by client name, associate name, rep ID…"
          value={docSearch}
          onChange={e => setDocSearch(e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 w-72 focus:outline-none focus:border-[#84ab4a]/50"
        />
        {isShare360Vertical && (
          <div className="flex items-center gap-1.5">
            {(["all", "business", "nonprofit"] as const).map(t => (
              <button
                key={t}
                onClick={() => setDocTypeFilter(t)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${docTypeFilter === t ? "bg-[#84ab4a] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
              >
                {t === "all" ? "All" : t === "business" ? "Business" : "Nonprofit"}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading documents…
        </div>
      ) : clientGroups.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <div className="text-sm font-medium">{docSearch ? "No documents match your search" : "No client documents yet"}</div>
        </div>
      ) : (
        <div className="space-y-2">
          {clientGroups.map(({ leadId, docs: clientDocs }) => {
            const first = clientDocs[0];
            const isExpanded = expandedClients.has(leadId);
            const pendingCount = clientDocs.filter((d: any) => d.status === "pending" || d.status === "submitted").length;
            const clientName = `${first.leadFirstName ?? ""} ${first.leadLastName ?? ""}`.trim() || "Unknown";
            const repName = repNameMap[first.leadRepCode ?? ""];
            const leadEmail = (first.leadEmail ?? "").toLowerCase().trim();
            const emailSubs = submissionsByEmail.get(leadEmail) ?? [];
            // Metadata fallback: if DocuSeal list didn't return the Merchant Info Form for
            // this lead via email (e.g. email mismatch or outside the 500-item window),
            // inject a synthetic entry from the submission ID stored in lead metadata.
            const meta = (first as any).leadMetadata as Record<string, any> | null | undefined;
            const merchantSubmissionId = meta?.s360_merchant_info?.docuseal_submission_id
              ? parseInt(meta.s360_merchant_info.docuseal_submission_id, 10)
              : null;
            const emailHasMerchant = emailSubs.some((s: any) => s.templateId === 3538142);
            const metaSub: any = (merchantSubmissionId && !isNaN(merchantSubmissionId) && !emailHasMerchant) ? {
              id: merchantSubmissionId,
              status: "completed",
              templateName: "Share360 Merchant Information Form",
              completedAt: meta?.s360_merchant_info_submitted_at ?? null,
              templateId: 3538142,
            } : null;
            const leadDocuSubs = metaSub ? [...emailSubs, metaSub] : emailSubs;
            const totalCount = clientDocs.length + leadDocuSubs.length;
            return (
              <div key={leadId} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Client header row */}
                <button
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  onClick={() => toggleClient(leadId)}
                >
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 flex-shrink-0">
                    {clientName[0]?.toUpperCase() ?? "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800">{clientName}</span>
                      {isShare360Vertical && clientTypeBadge(first.leadVerticalName ?? "")}
                      {pendingCount > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                          {pendingCount} pending
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-400 flex-wrap">
                      {first.leadBusinessName && <span>{first.leadBusinessName}</span>}
                      {first.leadRepCode && (
                        <span className="flex items-center gap-1">
                          <User className="w-2.5 h-2.5" />
                          {repName ? `${repName} (${first.leadRepCode})` : first.leadRepCode}
                        </span>
                      )}
                      <span className="text-gray-300">•</span>
                      <span>{totalCount} doc{totalCount !== 1 ? "s" : ""}</span>
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                </button>

                {/* Expanded doc list */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2">Document</th>
                          <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2">Status</th>
                          <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2">Date</th>
                          <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {clientDocs.map((doc: any) => (
                          <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                                <FileText className="w-3.5 h-3.5 text-gray-400" />
                                {doc.docLabel ?? doc.docType}
                              </div>
                              <div className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[220px]">{doc.fileName}</div>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${DOC_STATUS_STYLE[doc.status] ?? "bg-gray-100 text-gray-500"}`}>
                                {DOC_STATUS_LABEL[doc.status] ?? doc.status}
                              </span>
                              {doc.status === "rejected" && doc.reviewNote && (
                                <div className="mt-1.5 flex items-start gap-1 max-w-[200px]">
                                  <span className="text-[10px] font-bold text-red-600 whitespace-nowrap">Reason:</span>
                                  <span className="text-[10px] text-red-700 whitespace-pre-line leading-tight">{doc.reviewNote}</span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                              {new Date(doc.uploadedAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <a href={`/api/files/${doc.fileKey}`} target="_blank" rel="noopener noreferrer" title="View file"
                                  className="text-gray-400 hover:text-[#84ab4a] transition-colors">
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                                <button
                                  onClick={() => {
                                    setReviewingDoc(doc);
                                    setDecision(doc.status === "approved" || doc.status === "rejected" ? doc.status : "");
                                    setNote(doc.reviewNote ?? "");
                                  }}
                                  className="text-[10px] font-bold text-blue-600 hover:text-blue-800 border border-blue-200 rounded-lg px-2 py-0.5 transition-colors whitespace-nowrap"
                                >
                                  Review
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {leadDocuSubs.map((s: any) => (
                          <tr key={`ds-${s.id}`} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                                <FileText className="w-3.5 h-3.5 text-[#1A6FD4]" />
                                {s.templateName ?? `Submission #${s.id}`}
                              </div>
                              <div className="text-[10px] text-gray-400 mt-0.5">Signed form</div>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Signed
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                              {s.completedAt ? new Date(s.completedAt).toLocaleDateString() : "-"}
                            </td>
                            <td className="px-4 py-2.5">
                              <TeamSubmissionDocLink submissionId={s.id} status={s.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* DocuSeal Submissions - collapsible panel below portal documents */}
      <div className="mt-6 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors"
          onClick={() => setDocusealOpen(v => !v)}
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#1A6FD4]" />
            <span className="text-sm font-bold text-gray-800">DocuSeal Submissions</span>
            {docusealQuery.data && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                {docusealSubmissions.length}
              </span>
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${docusealOpen ? "rotate-180" : ""}`} />
        </button>

        {docusealOpen && (
          <div className="border-t border-gray-100 px-5 py-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search all history by email, name, template, or submission ID…"
                  value={docusealSearch}
                  onChange={e => setDocusealSearch(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 pr-8 w-full focus:outline-none focus:border-[#84ab4a]/50"
                />
                {isDocusealSearching && docusealSearchQuery.isFetching && (
                  <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" />
                )}
              </div>
              <button
                onClick={() => (isDocusealSearching ? docusealSearchQuery.refetch() : docusealQuery.refetch())}
                disabled={isDocusealSearching ? docusealSearchQuery.isFetching : docusealQuery.isFetching}
                className="p-1.5 border border-gray-200 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
              >
                {(isDocusealSearching ? docusealSearchQuery.isFetching : docusealQuery.isFetching)
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" />}
              </button>
            </div>

            {docusealSearch.trim().length === 1 && (
              <p className="text-[10px] text-amber-600 mb-2">Enter at least 2 characters to search.</p>
            )}

            {(isDocusealSearching ? docusealSearchQuery.isLoading : docusealQuery.isLoading) ? (
              <div className="py-8 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {isDocusealSearching ? "Searching all submission history…" : "Loading submissions…"}
              </div>
            ) : docusealSubmissions.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">
                {isDocusealSearching ? (
                  <>
                    <div>No submissions match "{docusealSearchTerm}".</div>
                    <div className="text-[10px] mt-1">
                      Searched all {(docusealSearchQuery.data?.indexedCount ?? 0).toLocaleString()} synchronized submissions.
                    </div>
                  </>
                ) : "No submissions found."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] font-bold uppercase text-gray-400 border-b border-gray-100">
                      <th className="text-left pb-2 pr-3">Template</th>
                      <th className="text-left pb-2 pr-3">Submitter</th>
                      <th className="text-left pb-2 pr-3">Status</th>
                      <th className="text-left pb-2 pr-3">Completed</th>
                      <th className="text-left pb-2">PDF</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {docusealSubmissions.map((s: any) => (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="py-2 pr-3 text-gray-700 max-w-[160px] truncate">{s.templateName ?? `#${s.id}`}</td>
                        <td className="py-2 pr-3 max-w-[180px]">
                          {s.submitterName && <div className="text-gray-800 truncate">{s.submitterName}</div>}
                          <div className="text-gray-400 truncate">{s.submitterEmail ?? "-"}</div>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                            s.status === "completed" ? "bg-green-50 text-green-700 border border-green-100" :
                            s.status === "pending" ? "bg-amber-50 text-amber-700 border border-amber-100" :
                            "bg-gray-100 text-gray-600 border border-gray-200"
                          }`}>{s.status}</span>
                        </td>
                        <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">
                          {s.completedAt ? new Date(s.completedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "-"}
                        </td>
                        <td className="py-2">
                          <TeamSubmissionDocLink submissionId={s.id} status={s.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Document review modal */}
      {reviewingDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <div className="text-sm font-bold text-gray-800">{reviewingDoc.docLabel ?? reviewingDoc.docType}</div>
                <div className="text-xs text-gray-400 mt-0.5">{reviewingDoc.fileName}</div>
              </div>
              <button onClick={() => setReviewingDoc(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2">Decision</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDecision("approved")}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border transition-all ${decision === "approved" ? "bg-emerald-50 border-emerald-400 text-emerald-700" : "border-gray-200 text-gray-500 hover:border-emerald-300"}`}
                  >
                    <CheckCircle className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button
                    onClick={() => setDecision("rejected")}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border transition-all ${decision === "rejected" ? "bg-red-50 border-red-400 text-red-700" : "border-gray-200 text-gray-500 hover:border-red-300"}`}
                  >
                    <XCircle className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                  Review Note
                  {decision === "rejected" && <span className="text-red-500 text-[10px]">(required for rejections)</span>}
                </label>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  rows={3}
                  placeholder={decision === "rejected" ? "Explain why this document is being rejected…" : "Optional note…"}
                  className="mt-1 w-full text-xs border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-[#84ab4a]/50 resize-none"
                />
              </div>
            </div>
            <div className="px-6 pb-5 flex justify-end gap-2">
              <button
                onClick={() => setReviewingDoc(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={!decision || reviewMutation.isPending || (decision === "rejected" && !note.trim())}
                onClick={() => reviewMutation.mutate({
                  docId: reviewingDoc.id,
                  status: decision as "approved" | "rejected",
                  reviewNote: note.trim() || undefined,
                  reviewedBy,
                })}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-white bg-[#84ab4a] hover:bg-[#6d9138] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              >
                {reviewMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                Submit Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Pipeline IDs per vertical (mirrors AdminDashboard VERTICALS config) ──────
// "Legacy360" here is the default/fallback pipeline ID for the vertical
// (Living Trust) - see LEGACY360_PIPELINE_ID_MAP below for the other 3
// Legacy360 pipelines sharing this same verticalName.
const TEAM_PIPELINE_IDS: Record<string, string> = {
  "Scale360":          "mzgOuQhHmkDuixcpiSbj",
  "Fundable360":       "UtddGTWZhyTcaRSWEGKm",
  "Share360 Nonprofit":"czZqnGQZru1yUcz7vHM6",
  "Share360 Business": "le4RyNh4YBlLMVeVslHt",
  "Share360":          "czZqnGQZru1yUcz7vHM6",
  "Legacy360":         "AtnyZ5hxQGhpBwTO8gro",
  "Media360":          "MKZZiFgADSTL2iNuOuvw",
};

// ─── Legacy360 pipeline sub-type routing (mirrors RepDashboard.tsx / AdminDashboard.tsx) ──
// Legacy360 has one verticalName ("Legacy360") shared by 4 distinct GHL pipelines.
// ghlPipelineId (set by CRM sync) is the authoritative signal; l360LeadType (set by
// the webhook push path) is the fallback. Living Trust is the default bucket.
type Legacy360PipelineKey = "living_trust" | "125plan" | "new_agent" | "advantage";
const LEGACY360_PIPELINE_ID_MAP: Record<string, Legacy360PipelineKey> = {
  "AtnyZ5hxQGhpBwTO8gro": "living_trust",
  "hoIcCAyZ8Wg0wPPNzosy": "125plan",
  "LHgTWf4RYWr6SZlbe94h": "new_agent",
  "EKY9Qps5kZDkRBbLjL4t": "advantage",
};
const LEGACY360_SUBTYPE_PIPELINE_IDS: Record<Exclude<Legacy360PipelineKey, "new_agent">, string> = {
  living_trust: "AtnyZ5hxQGhpBwTO8gro",
  "125plan":    "hoIcCAyZ8Wg0wPPNzosy",
  advantage:    "EKY9Qps5kZDkRBbLjL4t",
};
const LEGACY360_SUBTYPE_LABELS: Record<Legacy360PipelineKey, string> = {
  living_trust: "Living Trust",
  "125plan":    "125 Plan",
  new_agent:    "New Agents",
  advantage:    "Advantage",
};
function legacy360PipelineKeyForLead(lead: { l360LeadType?: string | null; ghlPipelineId?: string | null }): Legacy360PipelineKey {
  const byPipelineId = lead.ghlPipelineId ? LEGACY360_PIPELINE_ID_MAP[lead.ghlPipelineId] : undefined;
  if (byPipelineId) return byPipelineId;
  if (lead.l360LeadType === "legacy360_125plan") return "125plan";
  if (lead.l360LeadType === "legacy360_new_agent") return "new_agent";
  if (lead.l360LeadType === "lagecy360_advantage") return "advantage";
  return "living_trust";
}

// ─── Consultation Calendar URLs (mirrors AdminDashboard.tsx / RepDashboard.tsx) ──
// Team Portal contact cards reuse the exact same "Schedule Consultation"
// action/calendar routing as the associate and admin dashboards - see
// LeadDetailDrawer's ScheduleConsultationLink, which is gated behind
// consultation.checkEligibility (same server-enforced eligibility gate used
// everywhere else) and only renders when calendarUrls is non-empty. Before
// this, TeamMemberDashboard never passed calendarUrls to LeadDetailDrawer at
// all, so the button silently never appeared for Team Portal users even
// though the drawer already supported it.
const TEAM_CONSULTATION_CALENDARS: Record<string, { label: string; url: string; color: string }[]> = {
  "Fundable360":        [{ label: "Fundable360 Consultation",        url: "https://api.wibiz.ai/widget/booking/jN4jUeNAZmQ7BvM1aGZF", color: "#84ab4a" }],
  "Scale360":           [{ label: "Scale360 Consultation",           url: "https://api.wibiz.ai/widget/booking/O6uW585cnEvZAB2fNRmE", color: "#E8620A" }],
  "Share360":           [{ label: "Share360 Nonprofit Consultation", url: "https://api.wibiz.ai/widget/booking/eZS9bJf0uzV5aXN48jC8", color: "#0FA8A8" }],
  "Share360 Business":  [{ label: "Share360 Business Consultation",  url: "https://api.wibiz.ai/widget/booking/xBgDVk2OWeq0DePGpuY8", color: "#1A6FD4" }],
  "Share360 Nonprofit": [{ label: "Share360 Nonprofit Consultation", url: "https://api.wibiz.ai/widget/booking/eZS9bJf0uzV5aXN48jC8", color: "#0FA8A8" }],
  "Legacy360":          [{ label: "Legacy360 Living Trust",          url: "https://api.wibiz.ai/widget/booking/28XGN7D3DWQ4OSO2UWLn", color: "#C8920A" }],
  "Media360":           [{ label: "Media360 Consultation",           url: "https://api.wibiz.ai/widget/booking/f7Bih3G3zfHl0y4osogZ", color: "#7B4FD4" }],
};

// Legacy360's calendar, keyed by pipeline sub-type (not just verticalName) -
// all 4 Legacy360 pipelines share verticalName "Legacy360", so a
// verticalName-only lookup would always resolve to Living Trust's calendar
// regardless of the lead's actual pipeline.
const TEAM_LEGACY360_CONSULTATION_CALENDARS: Record<Legacy360PipelineKey, { label: string; url: string; color: string }[]> = {
  living_trust: [{ label: "Legacy360 Living Trust", url: "https://api.wibiz.ai/widget/booking/28XGN7D3DWQ4OSO2UWLn", color: "#C8920A" }],
  "125plan":    [{ label: "Legacy360 - Section 125 Plan Discovery Meeting", url: "https://api.wibiz.ai/widget/booking/Rt2jjmae3NaN5m53YxAi", color: "#C8920A" }],
  // New Agents is a certification roster, not a lead pipeline - no consultation booking applies.
  new_agent: [],
  // No client-supplied calendar for Advantage yet - matches RepDashboard.tsx/AdminDashboard.tsx (both empty).
  advantage: [],
};

function teamConsultationCalendarsForLead(lead: { verticalName?: string | null; l360LeadType?: string | null; ghlPipelineId?: string | null }): { label: string; url: string; color: string }[] {
  if (lead.verticalName === "Legacy360") {
    return TEAM_LEGACY360_CONSULTATION_CALENDARS[legacy360PipelineKeyForLead(lead)] ?? [];
  }
  return TEAM_CONSULTATION_CALENDARS[lead.verticalName ?? ""] ?? [];
}

// ─── Scoped Dashboard ─────────────────────────────────────────────────────────
function ScopedDashboard({ session, onLogout }: { session: TeamMemberSession; onLogout: () => void }) {
  // Full authorized list, preserved separately from the single active
  // vertical the UI is currently scoped to (server session field takes
  // precedence; falls back to parsing the raw comma-joined string for
  // sessions cached before this field existed).
  const authorizedVerticals = useMemo(
    () => (session.assignedVerticals?.length ? session.assignedVerticals : parseVerticalsClient(session.assignedVertical)),
    [session.assignedVerticals, session.assignedVertical]
  );

  const [activeVertical, setActiveVertical] = useState<string>(() => {
    try {
      const stored = sessionStorage.getItem("bc360_team_active_vertical");
      if (stored && authorizedVerticals.includes(stored)) return stored;
    } catch {}
    return authorizedVerticals[0] ?? session.assignedVertical;
  });

  const [activeTab, setActiveTab] = useState<"leads" | "appointments" | "agents" | "calendar" | "documents" | "agreements">("leads");
  // Stores only the id, not the lead object itself - the object is re-derived
  // from leadsQuery.data on every render (see `selectedLead` below) so it
  // always reflects the latest cache, including after a mutation invalidates
  // and refetches lead.list. Storing the whole object here previously froze
  // it at click time, so e.g. unchecking a checklist item would appear to
  // silently revert on the next render because the drawer kept reading this
  // stale snapshot instead of the freshly refetched lead - only a full page
  // reload (which re-derives selectedLead from scratch) showed the real state.
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [liveGhlUserId, setLiveGhlUserId] = useState<string | null>(session.ghlUserId);
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [sh360TypeFilter, setSh360TypeFilter] = useState<"all" | "business" | "nonprofit">("all");
  const [l360TypeFilter, setL360TypeFilter] = useState<"all" | Legacy360PipelineKey>("all");
  const [selectedAgent, setSelectedAgent] = useState<any | null>(null);

  // Switching verticals changes what every tab's data means - stale
  // leads/filters/selections from the previous vertical must not linger.
  // AgentsTab/DocumentsTab/AppointmentsTab are remounted via key={activeVertical}
  // (see render below), which resets their internal state automatically.
  function switchVertical(vertical: string) {
    if (vertical === activeVertical || !authorizedVerticals.includes(vertical)) return;
    setActiveVertical(vertical);
    try { sessionStorage.setItem("bc360_team_active_vertical", vertical); } catch {}
    setActiveTab("leads");
    setSelectedLeadId(null);
    setSelectedAgent(null);
    setSearchQuery("");
    setStageFilter(null);
    setSh360TypeFilter("all");
    setL360TypeFilter("all");
  }

  function handleGhlUserIdSaved(id: string) {
    setLiveGhlUserId(id);
    try {
      const stored = sessionStorage.getItem("bc360_team_session");
      if (stored) sessionStorage.setItem("bc360_team_session", JSON.stringify({ ...JSON.parse(stored), ghlUserId: id }));
    } catch {}
  }

  const leadsQuery = trpc.lead.list.useQuery(
    { verticalName: activeVertical },
    { refetchInterval: 30000 }
  );
  const leads = (leadsQuery.data ?? []) as any[];
  // Re-derived fresh on every render from the live query cache (never a
  // frozen snapshot) - see the comment on selectedLeadId above.
  const selectedLead = selectedLeadId !== null ? leads.find((l: any) => l.id === selectedLeadId) ?? null : null;

  // Stage movement - mirrors Admin functionality
  const utils = trpc.useUtils();
  const moveStage = trpc.lead.moveStage.useMutation({
    onSuccess: () => {
      toast.success("Stage updated");
      utils.lead.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  // Fetch all pipeline stages at load time - used for the Pipeline Breakdown section.
  // activePipelineId is computed after isShare360 is declared (see below) and passed via
  // a ref so the query input stays stable across renders until the filter actually changes.
  // NOTE: query input is set after isShare360 declaration - see allPipelineStagesQuery below.
  const allPipelineStagesQuery = trpc.lead.getGhlStages.useQuery(
    {
      pipelineId: (() => {
        const base = TEAM_PIPELINE_IDS[activeVertical];
        if (activeVertical.startsWith("Share360")) {
          if (sh360TypeFilter === "business")  return "le4RyNh4YBlLMVeVslHt";
          if (sh360TypeFilter === "nonprofit") return "czZqnGQZru1yUcz7vHM6";
          return base;
        }
        // Legacy360 has 4 pipelines sharing this verticalName - resolve to the
        // sub-type's actual pipeline ID so stage names shown reflect the right
        // pipeline. New Agents is certification-sourced, not a GHL pipeline -
        // no stages query applies (handled separately, see certRosterQuery).
        if (activeVertical === "Legacy360" && l360TypeFilter !== "all" && l360TypeFilter !== "new_agent") {
          return LEGACY360_SUBTYPE_PIPELINE_IDS[l360TypeFilter];
        }
        return base;
      })(),
      verticalName: activeVertical,
    },
    { staleTime: 300_000, enabled: !(activeVertical === "Legacy360" && l360TypeFilter === "new_agent") }
  );
  const allPipelineStages: { id: string; name: string }[] = allPipelineStagesQuery.data ?? [];

  // Fetch GHL stages for the selected lead's pipeline (used in the stage-move dropdown).
  // Legacy360 needs its pipeline sub-type resolved per-lead (all 4 pipelines share
  // verticalName "Legacy360") - otherwise every lead would show Living Trust's
  // stage options in the move-stage dropdown, even for 125 Plan/Advantage leads.
  const pipelineIdForLead = selectedLead
    ? selectedLead.verticalName === "Legacy360"
      ? (() => {
          const key = legacy360PipelineKeyForLead(selectedLead);
          return key === "new_agent" ? undefined : LEGACY360_SUBTYPE_PIPELINE_IDS[key];
        })()
      : TEAM_PIPELINE_IDS[selectedLead.verticalName] ?? undefined
    : undefined;
  const ghlStagesQuery = trpc.lead.getGhlStages.useQuery(
    { verticalName: selectedLead?.verticalName ?? undefined, pipelineId: pipelineIdForLead },
    { enabled: selectedLead !== null }
  );

  const repNameQuery = trpc.rep.listByVertical.useQuery(
    { vertical: activeVertical },
    { staleTime: 300_000 }
  );
  const repNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    (repNameQuery.data ?? []).forEach((r: any) => {
      if (r.repCode) map[r.repCode] = `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim();
    });
    return map;
  }, [repNameQuery.data]);

  const isShare360 = activeVertical.startsWith("Share360");
  const isLegacy360 = activeVertical === "Legacy360";
  const filteredLeads = leads.filter(l => {
    const stageKey = (l as any).ghlStageName || stageLabel(l.stage);
    if (stageFilter && stageKey !== stageFilter) return false;
    if (isShare360 && sh360TypeFilter !== "all") {
      // verticalName is the authoritative sub-type; sh360Role is GHL-synced and can lag behind
      const role = (l.verticalName === "Share360 Business" ? "business" :
                    l.verticalName === "Share360 Nonprofit" ? "nonprofit" :
                    (l as any).sh360Role || "");
      if (sh360TypeFilter === "business" && role !== "business") return false;
      if (sh360TypeFilter === "nonprofit" && role !== "nonprofit") return false;
    }
    if (isLegacy360 && l360TypeFilter !== "all") {
      // New Agents isn't lead/opportunity data at all - its roster is rendered
      // separately (see certRosterQuery), so it never matches a lead row here.
      if (l360TypeFilter === "new_agent") return false;
      if (legacy360PipelineKeyForLead(l as any) !== l360TypeFilter) return false;
    }
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      l.firstName?.toLowerCase().includes(q) ||
      l.lastName?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.businessName?.toLowerCase().includes(q) ||
      l.repCode?.toLowerCase().includes(q)
    );
  });

  // Stats
  const totalLeads = leads.length;
  const activeLeads = leads.filter(l => !["lost_cold", "inactive"].includes(l.stage)).length;
  const enrolled = leads.filter(l => ["active_client", "enrolled", "onboarding_submitted"].includes(l.stage)).length;
  const thisWeek = leads.filter(l => {
    const d = new Date(l.createdAt);
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return d > weekAgo;
  }).length;

  // Stage distribution - for Share360/Legacy360, respect the type filter so counts reflect the selected track
  const sh360TypeLeads = isShare360 && sh360TypeFilter !== "all"
    ? leads.filter(l => {
        const role = (l.verticalName === "Share360 Business" ? "business" :
                      l.verticalName === "Share360 Nonprofit" ? "nonprofit" :
                      (l as any).sh360Role || "");
        return sh360TypeFilter === role;
      })
    : isLegacy360 && l360TypeFilter !== "all" && l360TypeFilter !== "new_agent"
    ? leads.filter(l => legacy360PipelineKeyForLead(l as any) === l360TypeFilter)
    : leads;
  const stageCounts: Record<string, number> = {};
  sh360TypeLeads.forEach(l => {
    const key = (l as any).ghlStageName || stageLabel(l.stage);
    stageCounts[key] = (stageCounts[key] ?? 0) + 1;
  });

  // New Agents roster (certification-sourced, not GHL opportunities) - mirrors
  // AdminDashboard's isCertificationPipeline branch. Only fetched when the
  // New Agents sub-tab is active.
  const certRosterQuery = trpc.certification.adminList.useQuery(
    { vertical: "Legacy360", status: "passed" },
    { enabled: isLegacy360 && l360TypeFilter === "new_agent" }
  );

  const VERTICAL_COLORS: Record<string, string> = {
    "Scale360": "#E8620A",
    "Fundable360": "#84ab4a",
    "Legacy360": "#C8920A",
    "Share360 Business": "#1A6FD4",
    "Share360 Nonprofit": "#0FA8A8",
    "Share360": "#1A6FD4",
    "Media360": "#9333EA",
  };
  const vertColor = VERTICAL_COLORS[activeVertical] ?? "#84ab4a";

  return (
    <div className="flex min-h-screen bg-[#F5F6F8]">
      {/* Sidebar */}
      <aside className="w-56 bg-[#0D1E35] flex flex-col fixed inset-y-0 left-0 z-40">
        <div className="p-4 border-b border-white/7">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${vertColor}20`, border: `1px solid ${vertColor}40` }}>
              <LayoutDashboard className="w-4 h-4" style={{ color: vertColor }} />
            </div>
            <div>
              <div className="text-sm font-bold text-white">Team Portal</div>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: vertColor }}>
                {activeVertical}
                {session.role === "product_manager" && (
                  <span className="ml-1 text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300">PM</span>
                )}
              </div>
            </div>
          </div>
          {/* Vertical switcher - only shown for members assigned more than one
              vertical; single-vertical members keep the plain label above with
              no extra UI. */}
          {authorizedVerticals.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {authorizedVerticals.map(v => (
                <button
                  key={v}
                  onClick={() => switchVertical(v)}
                  className={`text-[10px] font-semibold px-2 py-1 rounded-full border transition-all ${
                    v === activeVertical
                      ? "text-white border-transparent"
                      : "text-white/50 border-white/10 hover:text-white hover:border-white/30"
                  }`}
                  style={v === activeVertical ? { background: VERTICAL_COLORS[v] ?? "#84ab4a" } : {}}
                >
                  {v}
                </button>
              ))}
            </div>
          )}
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {([
            { key: "leads", label: "Leads", Icon: Users },
            { key: "appointments", label: "Appointments", Icon: Calendar },
            { key: "agents", label: "Agents", Icon: Users2 },
            { key: "calendar", label: "BC360 Calendar", Icon: FolderOpen },
            { key: "documents", label: "Client Docs", Icon: FileText },
            { key: "agreements", label: "Agreements", Icon: FileText },
          ] as { key: "leads" | "appointments" | "agents" | "calendar" | "documents" | "agreements"; label: string; Icon: any }[]).map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                activeTab === key ? "text-white" : "text-white/50 hover:text-white hover:bg-white/5"
              }`}
              style={activeTab === key ? { background: `${vertColor}20`, color: vertColor } : {}}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-white/7">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs font-semibold text-white truncate">{session.name}</div>
            <div className="text-[10px] text-white/40 truncate">{session.email}</div>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/50 hover:text-white hover:bg-white/5 transition-all"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="ml-56 flex-1 p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">{activeVertical} Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {activeTab === "leads"
            ? `Viewing all leads for ${activeVertical}`
            : activeTab === "appointments"
            ? "Your upcoming and past appointments"
            : activeTab === "calendar"
            ? "Company-wide training sessions and events"
            : activeTab === "documents"
            ? `Client documents submitted through ${activeVertical}`
            : activeTab === "agreements"
            ? "Agreements assigned to you for company signature"
            : `Active agents in ${activeVertical}`}
          </p>
        </div>

        {/* Appointments tab */}
        {activeTab === "appointments" && (
          liveGhlUserId
            ? <AppointmentsTab
                key={activeVertical}
                userId={liveGhlUserId}
                vertical={activeVertical}
                memberId={session.id}
                onUserIdSaved={handleGhlUserIdSaved}
              />
            : <StaffIdSetupPanel memberId={session.id} onSaved={handleGhlUserIdSaved} />
        )}

        {/* Agents tab */}
        {activeTab === "agents" && (
          <AgentsTab key={activeVertical} vertical={activeVertical} onAgentClick={setSelectedAgent} />
        )}

        {/* Calendar tab */}
        {activeTab === "calendar" && <CalendarTab />}

        {/* Documents tab */}
        {activeTab === "documents" && <DocumentsTab key={activeVertical} vertical={activeVertical} repNameMap={repNameMap} reviewedBy={session.name} />}

        {activeTab === "agreements" && <AgreementCompanyQueue />}

        {/* Leads tab content */}
        {activeTab === "leads" && <>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Leads", value: totalLeads, icon: Users, color: vertColor },
            { label: "Active", value: activeLeads, icon: BarChart3, color: "#10B981" },
            { label: "Enrolled/Active", value: enrolled, icon: ChevronRight, color: "#8B5CF6" },
            { label: "This Week", value: thisWeek, icon: Clock, color: "#F59E0B" },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</div>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
                  <Icon className="w-3.5 h-3.5" style={{ color }} />
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900">{value}</div>
            </div>
          ))}
        </div>

        {/* Legacy360 pipeline sub-type filter - all 4 pipelines share verticalName "Legacy360" */}
        {isLegacy360 && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-1">Pipeline:</span>
            {(["all", "living_trust", "125plan", "advantage"] as const).map(t => (
              <button
                key={t}
                onClick={() => { setL360TypeFilter(t); setStageFilter(null); }}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
                  l360TypeFilter === t ? "bg-[#C8920A] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {t === "all" ? "All" : LEGACY360_SUBTYPE_LABELS[t]}
              </button>
            ))}
          </div>
        )}

        {/* New Agents - certification roster (not a lead/opportunity board) */}
        {isLegacy360 && l360TypeFilter === "new_agent" ? (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Information Submitted</span>
              <span className="text-xs font-bold rounded-full px-1.5 py-0.5 ml-1 bg-[#C8920A]/15 text-[#C8920A]">
                {(certRosterQuery.data ?? []).length}
              </span>
            </div>
            {certRosterQuery.isLoading ? (
              <div className="py-8 text-center text-gray-400 text-sm">Loading certified associates...</div>
            ) : (certRosterQuery.data ?? []).length === 0 ? (
              <div className="bg-white/50 border border-dashed border-[#C8920A]/30 rounded-xl p-8 text-center">
                <div className="text-sm text-gray-400">No Legacy360-certified associates yet</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {(certRosterQuery.data ?? []).map((cert: any) => (
                  <div key={cert.id} className="bg-white border border-gray-100 rounded-xl p-3">
                    <div className="font-semibold text-xs text-gray-800 truncate">
                      {[cert.repFirstName, cert.repLastName].filter(Boolean).join(" ") || cert.repCode}
                    </div>
                    <div className="text-xs text-gray-400 truncate mt-0.5">{cert.repCode}</div>
                    {cert.completedAt && (
                      <div className="text-[10px] text-gray-400 mt-1">
                        Certified {new Date(cert.completedAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : <>

        {/* Stage breakdown - shows all configured pipeline stages in order, including empty ones */}
        {(allPipelineStages.length > 0 || Object.keys(stageCounts).length > 0) && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Pipeline Breakdown</div>
              {stageFilter && (
                <button onClick={() => setStageFilter(null)} className="text-xs text-[#84ab4a] hover:underline">Clear filter</button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {(allPipelineStages.length > 0
                // Render in configured pipeline order, showing all stages (count 0 for empty ones)
                ? allPipelineStages.map(s => ({ stage: s.name, count: stageCounts[s.name] ?? 0 }))
                // Fallback: no pipeline config yet, show only stages with leads (sorted by count)
                : Object.entries(stageCounts).sort((a, b) => b[1] - a[1]).map(([stage, count]) => ({ stage, count }))
              ).map(({ stage, count }) => (
                <button
                  key={stage}
                  onClick={() => count > 0 ? setStageFilter(stageFilter === stage ? null : stage) : undefined}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all"
                  style={{
                    background: stageFilter === stage ? stageColor(stage) : `${stageColor(stage)}15`,
                    color: stageFilter === stage ? "#fff" : count === 0 ? "#9CA3AF" : stageColor(stage),
                    outline: stageFilter === stage ? `2px solid ${stageColor(stage)}` : "none",
                    cursor: count === 0 ? "default" : "pointer",
                    opacity: count === 0 ? 0.5 : 1,
                  }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: stageFilter === stage ? "#fff" : count === 0 ? "#9CA3AF" : stageColor(stage) }} />
                  {stage} ({count})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Share360 type filter */}
        {isShare360 && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-1">Type:</span>
            {(["all", "business", "nonprofit"] as const).map(t => (
              <button
                key={t}
                onClick={() => { setSh360TypeFilter(t); setStageFilter(null); }}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
                  sh360TypeFilter === t ? "bg-[#84ab4a] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {t === "all" ? "All" : t === "business" ? "Business" : "Nonprofit"}
              </button>
            ))}
          </div>
        )}

        {/* Leads table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="text-sm font-bold text-gray-700">All Leads ({filteredLeads.length})</div>
            <input
              type="text"
              placeholder="Search leads..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 w-48 focus:outline-none focus:border-[#84ab4a]/50"
            />
          </div>

          {leadsQuery.isLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading leads...
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <div className="text-sm">No leads found for {activeVertical}</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Name</th>
                    <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Business / Org Name</th>
                    <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Contact</th>
                    <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Application Details</th>
                    <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Stage</th>
                    <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Rep</th>
                    <th className="text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider px-4 py-2.5">Booked</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredLeads.map(lead => (
                    <tr
                      key={lead.id}
                      onClick={() => setSelectedLeadId(lead.id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        {(() => {
                          // Always contact person name, rep-code stripped
                          const raw = [lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.email?.split("@")[0] || "Unknown";
                          const contactName = stripRepCode(raw, lead.repCode) ?? raw;
                          const initial = ((stripRepCode(lead.firstName, lead.repCode) || lead.firstName || lead.email || "?")[0] ?? "?").toUpperCase();
                          return (
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 flex-shrink-0">
                                {initial}
                              </div>
                              <div className="text-sm font-semibold text-gray-800">{contactName}</div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 max-w-[180px]">
                        {(() => {
                          const isS360 = (lead.verticalName ?? "").startsWith("Share360");
                          // For Share360: prefer dedicated org/business name fields.
                          // Fall back to metadata["Organization Name"] / metadata["Business Name"]
                          // for leads whose form submissions landed in the metadata catch-all
                          // before the dedicated sh360* columns were mapped.
                          // Fall back to businessName only when it doesn't look like the
                          // contact person's name (i.e. it differs from firstName+lastName).
                          let raw: string | null = null;
                          if (isS360) {
                            const orgField = (lead as any).sh360OrgName || (lead as any).sh360BusinessName || null;
                            if (orgField) {
                              raw = orgField;
                            } else {
                              // Check metadata catch-all
                              const meta = (lead as any).metadata as Record<string, unknown> | null | undefined;
                              const metaOrg = meta
                                ? String(meta["SH360 Org Name"] || meta["SH360 Business Name"] || meta["Organization Name"] || meta["Business Name"] || meta["Org Name"] || "").trim()
                                : "";
                              if (metaOrg) {
                                raw = metaOrg;
                              } else if (lead.businessName) {
                                // Only show businessName when it isn't just the contact person's name
                                const contactFull = [lead.firstName, lead.lastName].filter(Boolean).join(" ");
                                const stripped = stripRepCode(lead.businessName, lead.repCode) ?? lead.businessName;
                                const strippedContact = stripRepCode(contactFull, lead.repCode) ?? contactFull;
                                if (stripped !== strippedContact) raw = lead.businessName;
                              }
                            }
                          } else {
                            raw = (lead as any).f360BusinessName || lead.businessName || null;
                          }
                          const orgName = raw ? (stripRepCode(raw, lead.repCode) ?? raw) : null;
                          return orgName
                            ? <div className="text-sm text-gray-700 font-medium truncate">{orgName}</div>
                            : <span className="text-xs text-gray-300">-</span>;
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          {lead.email && <div className="text-xs text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</div>}
                          {lead.phone && <div className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</div>}
                        </div>
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <div className="space-y-0.5">
                          {isShare360 && (() => {
                            const r = (lead.verticalName === "Share360 Business" ? "business" :
                                       lead.verticalName === "Share360 Nonprofit" ? "nonprofit" :
                                       (lead as any).sh360Role || "");
                            return r ? (
                              <span className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full ${r === "nonprofit" ? "bg-teal-50 text-teal-700" : "bg-blue-50 text-blue-700"}`}>
                                {r === "nonprofit" ? "Nonprofit" : "Business"}
                              </span>
                            ) : null;
                          })()}
                          {(lead as any).f360Industry && (
                            <div className="text-xs text-gray-600 flex items-center gap-1"><Tag className="w-2.5 h-2.5 text-gray-400" />{(lead as any).f360Industry}</div>
                          )}
                          {(lead as any).f360Revenue && (
                            <div className="text-xs text-gray-500">Rev: {(lead as any).f360Revenue}</div>
                          )}
                          {(lead as any).f360CreditStatus && (
                            <div className="text-xs text-gray-500">Credit: {(lead as any).f360CreditStatus}</div>
                          )}
                          {(lead as any).f360TimeInBusiness && (
                            <div className="text-xs text-gray-500">TIB: {(lead as any).f360TimeInBusiness}</div>
                          )}
                          {!isShare360 && !(lead as any).f360Industry && !(lead as any).f360Revenue && !(lead as any).f360CreditStatus && (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${stageColor(lead.stage)}15`, color: stageColor(lead.stage) }}>
                          {(lead as any).ghlStageName || stageLabel(lead.stage)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {lead.repCode ? (
                          <div>
                            {repNameMap[lead.repCode] && (
                              <div className="text-xs font-semibold text-gray-700">{repNameMap[lead.repCode]}</div>
                            )}
                            <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{lead.repCode}</span>
                          </div>
                        ) : <span className="text-xs text-gray-300">-</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock className="w-3 h-3" />
                          {timeAgo(lead.createdAt)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>}
        {/* End leads tab */}
        </>}
      </main>

      {/* Lead Detail Drawer - team members can move pipeline stages */}
      {selectedLead && (
        <LeadDetailDrawer
          lead={selectedLead}
          onClose={() => setSelectedLeadId(null)}
          isAdmin={true}
          canManageShare360InternalSteps={true}
          authorName={session.name}
          teamMemberId={session.id}
          canSendPortal={true}
          canTriggerCommission={session.role === "product_manager"}
          onGhlStageChange={(leadId, ghlStageId, ghlStageName) => {
            moveStage.mutate({ leadId, ghlStageId, ghlStageName, changedBy: session.name, isAdmin: true });
          }}
          ghlStages={ghlStagesQuery.data ?? []}
          calendarUrls={teamConsultationCalendarsForLead(selectedLead as any)}
        />
      )}

      {/* Agent Detail Modal */}
      {selectedAgent && <AgentDetailModal agent={selectedAgent} onClose={() => setSelectedAgent(null)} />}
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function TeamMemberDashboard() {
  const [session, setSession] = useState<TeamMemberSession | null>(() => {
    try {
      const stored = sessionStorage.getItem("bc360_team_session");
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const handleLogin = (s: TeamMemberSession) => {
    sessionStorage.setItem("bc360_team_session", JSON.stringify(s));
    setSession(s);
  };

  const logoutMutation = trpc.teamMembers.logout.useMutation();
  const handleLogout = () => {
    sessionStorage.removeItem("bc360_team_session");
    setSession(null);
    toast.success("Signed out");
    // Clear the server-side session cookie too - sessionStorage removal alone
    // leaves the httpOnly team-manager session valid for anyone else on this
    // browser until it expires.
    logoutMutation.mutate();
  };

  if (!session) return <TeamMemberLogin onLogin={handleLogin} />;
  return <ScopedDashboard session={session} onLogout={handleLogout} />;
}
